import type { ScanResult, ScanVerdict } from '@/screens/scan/types'

/** Scan verdict colors — match ramp-web-app ScanItemRowProgress / AiScanDetail. */
export const FAIL = '#f87171'
export const PASS = '#22c55e'
export const ERROR = '#ff9800'

export const SCAN_VERDICT_COLORS: Record<ScanVerdict, string> = {
  Pass: PASS,
  Fail: FAIL,
  Error: ERROR
}

export function verdictColor(status?: ScanVerdict): string | undefined {
  return status ? SCAN_VERDICT_COLORS[status] : undefined
}

/** BEM-style modifier for verdict-specific block styling. */
export const SCAN_VERDICT_MODIFIERS: Record<ScanVerdict, string> = {
  Pass: 'is-pass',
  Fail: 'is-fail',
  Error: 'is-error'
}

export function verdictModifier(status?: ScanVerdict): string {
  return status ? SCAN_VERDICT_MODIFIERS[status] : ''
}

/** One active text-block highlight across the session list (only one at a time). */
export interface SelectedHighlight {
  resultKey: string
  blockIndex: number
}

export function resultKey(row: ScanResult): string {
  return row._id ?? row.checklistItemId
}

export interface Tally {
  pass: number
  fail: number
  error: number
  pending: number
}

export const tally = (results: ScanResult[]): Tally =>
  results.reduce<Tally>(
    (acc, row) => {
      if (row.status === 'INITIAL') acc.pending += 1
      else if (row.status === 'ERROR' || row.scanStatus === 'Error') acc.error += 1
      else if (row.scanStatus === 'Pass') acc.pass += 1
      else if (row.scanStatus === 'Fail') acc.fail += 1
      return acc
    },
    { pass: 0, fail: 0, error: 0, pending: 0 }
  )

/** "Jun 18, 2:04 PM" with a relative suffix ("just now", "3 days ago"). */
export function formatWhen(iso?: string): string {
  if (!iso) return 'Unknown date'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return 'Unknown date'
  const absolute = date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
  const diffMs = Date.now() - date.getTime()
  const mins = Math.round(diffMs / 60000)
  let relative: string
  if (mins < 1) relative = 'just now'
  else if (mins < 60) relative = `${mins}m ago`
  else if (mins < 1440) relative = `${Math.round(mins / 60)}h ago`
  else relative = `${Math.round(mins / 1440)}d ago`
  return `${absolute} · ${relative}`
}

export const segments = (t: Tally): Array<{ color: string; count: number }> =>
  [
    { color: FAIL, count: t.fail },
    { color: PASS, count: t.pass },
    { color: ERROR, count: t.error }
  ].filter((segment) => segment.count > 0)
